import { useCallback, useEffect, useMemo, useState } from 'react'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import Pagination, { paginate } from 'components/Pagination/Pagination'
import { Stylable } from 'utils/types'
import {
  DisplayEvent,
  EventNames,
  getDecodedEvents,
  getFiltersFromLocalStorage,
  saveFiltersToLocalStorage,
} from './util'
import { EventDetailsDialog } from './EventDetailsDialog'
import { Header } from './Header'
import { LpRow } from './LpRow'
import { Container, EmptyTable } from './styles'
import { ActivityHistoryContentLoader } from './ActivityHistoryContentLoader'
import { Typography } from 'components/Typography/Typography'

interface Props extends Stylable {
  positionInfo: PositionInfo
  events: {
    transactionDigest: string
    timestampMs: number
    eventType: string
    bcsBytes: string
  }[]
  isLoading: boolean
}

const PAGE_SIZE = 10

const FILTERS_KEY = 'activity-history-filters'

const defaultFilters = {
  [EventNames.PositionCreationInfo]: true,
  [EventNames.AddCollateralInfo]: true,
  [EventNames.RebalanceInfo]: true,
  [EventNames.ReductionInfo]: true,
  [EventNames.DeleverageInfo]: true,
  [EventNames.LiquidationInfo]: true,
  [EventNames.DeletePositionInfo]: true,
  [EventNames.OwnerCollectFeeInfo]: true,
  [EventNames.OwnerCollectRewardInfo]: true,
  [EventNames.OwnerTakeStashedRewardsInfo]: true,
}

export const LpActivityHistory = ({ positionInfo, events, isLoading, style }: Props) => {
  const [filters, setFilters] = useState<Record<string, boolean>>(() =>
    getFiltersFromLocalStorage(FILTERS_KEY, positionInfo.positionId, defaultFilters)
  )

  const positionId = positionInfo.positionId

  const sortedEvents = useMemo(() => {
    return [...events].reverse()
  }, [events])

  const formattedActivityEvents = useMemo(() => {
    return getDecodedEvents(positionInfo.lastPosition.configInfo, sortedEvents)
  }, [positionInfo.lastPosition.configInfo, sortedEvents])

  const displayEvents = useMemo(() => {
    const selectedEventTypes = Object.keys(filters).filter(key => filters[key] === true)
    return formattedActivityEvents.filter(e => {
      return selectedEventTypes.indexOf(e.action) >= 0
    })
  }, [formattedActivityEvents, filters])

  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [sortedEvents])

  useEffect(() => {
    const positionFilters = getFiltersFromLocalStorage(FILTERS_KEY, positionId, defaultFilters)
    setFilters(positionFilters)
  }, [positionId])

  const [activeEvent, setActiveEvent] = useState<DisplayEvent>()

  const handleEventRowClick = (event: DisplayEvent) => setActiveEvent(event)
  const handleCloseEvent = () => setActiveEvent(undefined)

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handleFilterClick = useCallback(
    (filterName: keyof typeof filters) => {
      const newFilters = {
        ...filters,
        [filterName]: !filters[filterName],
      }
      setFilters(newFilters)
      saveFiltersToLocalStorage(FILTERS_KEY, positionId, newFilters)
      setCurrentPage(1)
    },
    [filters, setCurrentPage, positionId]
  )

  const paginatedItems = paginate(displayEvents, PAGE_SIZE, currentPage)

  const totalPages = Math.ceil(displayEvents.length / PAGE_SIZE)

  if (isLoading) {
    return (
      <Container>
        <Header
          filters={filters}
          handleFilterClick={handleFilterClick}
          filterItems={Object.keys(defaultFilters)}
          disabledFilter
        />
        <ActivityHistoryContentLoader />
      </Container>
    )
  }

  return (
    <>
      <Container style={style}>
        <Header
          filters={filters}
          handleFilterClick={handleFilterClick}
          filterItems={Object.keys(defaultFilters)}
          disabledFilter={events?.length === 0}
        />
        {paginatedItems?.length === 0 && (
          <EmptyTable>
            <Typography variant="label2" fontWeight="semiBold" color="secondary">
              No events to display
            </Typography>
          </EmptyTable>
        )}
        {paginatedItems.map((event, index) => (
          <LpRow
            key={`${index}-${event.transactionDigest}`}
            event={event}
            isLast={index === paginatedItems.length - 1}
            handleEventRowClick={handleEventRowClick}
          />
        ))}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          type={`lp-activity-history-${positionId}`}
        />
      </Container>
      {activeEvent && (
        <EventDetailsDialog
          kind={activeEvent.action}
          timestampMs={activeEvent.timestampMs}
          transactionDigest={activeEvent.transactionDigest}
          details={activeEvent.details}
          onClose={handleCloseEvent}
        />
      )}
    </>
  )
}
